import React, { useEffect, useMemo, useRef, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import {
  HiMenu,
  HiSearch,
  HiChevronLeft,
  HiChevronRight,
  HiPaperClip,
  HiDownload,
  HiX,
} from 'react-icons/hi';
import Toast from '../../components/toaster';
import { uploadFile } from '../../services/assetService';
import {
  fetchSupportChatsList,
  fetchSupportChatRoom,
  sendSupportChatMessage,
} from '../../services/helpSupportService';

const CHAT_PAGE_SIZE = 20;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const getString = (value) => (typeof value === 'string' ? value : '');

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

const formatTimeShort = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const truncate = (text = '', length = 65) => {
  if (!text) return '';
  return text.length > length ? `${text.slice(0, length)}...` : text;
};

const extractChatRows = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

const extractMessages = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

const getTotalCount = (response, listLength, currentPage, pageSize) => {
  const total =
    response?.totalCount ??
    response?.total ??
    response?.data?.totalCount ??
    response?.data?.total ??
    response?.data?.pagination?.total ??
    null;

  if (typeof total === 'number') return total;

  if (listLength === pageSize) {
    return currentPage * pageSize + 1;
  }

  if (listLength > 0) {
    return (currentPage - 1) * pageSize + listLength;
  }

  return 0;
};

const normalizeChatRoom = (item, index) => {
  const user = item?.user || {};
  const id = item?.user_id || user?._id || item?._id || `support-chat-${index}`;

  return {
    id,
    name: getString(user?.name) || 'Unknown User',
    email: getString(user?.email),
    avatar: getString(user?.profile_picture),
    lastMessage: getString(item?.last_message),
    lastMessageType: getString(item?.last_message_type) || 'text',
    lastSenderType: getString(item?.last_sender_type) || 'user',
    lastMessageAt: item?.last_message_at || null,
    totalMessages: Number(item?.total_messages || 0),
  };
};

const normalizeMessage = (item, index) => ({
  id: item?._id || `support-msg-${index}`,
  userId: item?.user_id || '',
  senderType: getString(item?.sender_type) || 'user',
  text: getString(item?.text),
  fileUrls: Array.isArray(item?.fileUrls)
    ? item.fileUrls.filter((url) => typeof url === 'string' && url.trim())
    : [],
  type: getString(item?.type) || 'text',
  createdAt: item?.createdAt || null,
});

const getFileNameFromUrl = (url) => {
  try {
    const pathname = new URL(url).pathname;
    const fileName = pathname.split('/').pop();
    return fileName || 'attachment';
  } catch (error) {
    const clean = getString(url).split('?')[0];
    const fileName = clean.split('/').pop();
    return fileName || 'attachment';
  }
};

const downloadFile = (url) => {
  if (!url) return;

  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = getFileNameFromUrl(url);
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    window.open(url, '_blank');
  }
};

const HelpSupportRequests = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [chatRooms, setChatRooms] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomsError, setRoomsError] = useState('');
  const [roomsRefreshKey, setRoomsRefreshKey] = useState(0);

  const [selectedChatId, setSelectedChatId] = useState('');
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState('');

  const [messageText, setMessageText] = useState('');
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imageValidationError, setImageValidationError] = useState('');
  const [sending, setSending] = useState(false);

  const searchDebounceRef = useRef(null);
  const messagesEndRef = useRef(null);

  const totalPages = Math.ceil(totalRecords / CHAT_PAGE_SIZE);

  const selectedRoom = useMemo(
    () => chatRooms.find((room) => room.id === selectedChatId) || null,
    [chatRooms, selectedChatId]
  );

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 500);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    let cancelled = false;

    const loadSupportRooms = async () => {
      setRoomsLoading(true);
      setRoomsError('');

      try {
        const response = await fetchSupportChatsList(
          currentPage,
          CHAT_PAGE_SIZE,
          debouncedSearch,
          '',
          ''
        );

        if (cancelled) return;

        const rows = extractChatRows(response);
        const normalized = rows.map((item, index) => normalizeChatRoom(item, index));

        setChatRooms(normalized);
        setTotalRecords(getTotalCount(response, normalized.length, currentPage, CHAT_PAGE_SIZE));

        setSelectedChatId((previousSelectedId) => {
          if (normalized.length === 0) {
            return '';
          }

          const hasSelected = normalized.some((room) => room.id === previousSelectedId);
          return hasSelected ? previousSelectedId : normalized[0].id;
        });

        if (normalized.length === 0) {
          setMessages([]);
        }
      } catch (error) {
        if (cancelled) return;
        setRoomsError(error.message || 'Failed to load support chats.');
        setChatRooms([]);
        setTotalRecords(0);
      } finally {
        if (!cancelled) {
          setRoomsLoading(false);
        }
      }
    };

    loadSupportRooms();

    return () => {
      cancelled = true;
    };
  }, [currentPage, debouncedSearch, roomsRefreshKey]);

  useEffect(() => {
    if (!selectedChatId) {
      setMessages([]);
      setMessagesError('');
      return;
    }

    let cancelled = false;

    const loadMessages = async () => {
      setMessagesLoading(true);
      setMessagesError('');

      try {
        const response = await fetchSupportChatRoom(selectedChatId);
        if (cancelled) return;

        const rows = extractMessages(response);
        const normalized = rows.map((item, index) => normalizeMessage(item, index));
        setMessages(normalized);
      } catch (error) {
        if (cancelled) return;
        setMessagesError(error.message || 'Failed to load chat messages.');
        setMessages([]);
      } finally {
        if (!cancelled) {
          setMessagesLoading(false);
        }
      }
    };

    loadMessages();

    return () => {
      cancelled = true;
    };
  }, [selectedChatId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleImageSelection = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setImageValidationError('Only image files are allowed.');
      Toast.error('Only image files are allowed.');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      setImageValidationError('Image size must be 5MB or less.');
      Toast.error('Image size must be 5MB or less.');
      return;
    }

    setSelectedImageFile(file);
    setImageValidationError('');
  };

  const clearSelectedImage = () => {
    setSelectedImageFile(null);
    setImageValidationError('');
  };

  const handleSendMessage = async () => {
    if (!selectedChatId || sending) return;

    const normalizedText = messageText.trim();

    if (!normalizedText && !selectedImageFile) {
      Toast.error('Enter a message or attach one image.');
      return;
    }

    setSending(true);

    try {
      let uploadedImageUrl = '';

      if (selectedImageFile) {
        if (!selectedImageFile.type.startsWith('image/')) {
          throw new Error('Only image files are allowed.');
        }

        if (selectedImageFile.size > MAX_IMAGE_SIZE) {
          throw new Error('Image size must be 5MB or less.');
        }

        uploadedImageUrl = await uploadFile(selectedImageFile);
      }

      const response = await sendSupportChatMessage(selectedChatId, {
        text: normalizedText,
        fileUrls: uploadedImageUrl ? [uploadedImageUrl] : [],
        type: uploadedImageUrl ? 'image' : 'text',
      });

      const sentMessageRaw =
        response?.data?.sent_message ||
        response?.sent_message ||
        response?.data ||
        null;

      if (sentMessageRaw) {
        const nextMessage = normalizeMessage(sentMessageRaw, messages.length);
        setMessages((prev) => [...prev, nextMessage]);

        setChatRooms((prev) => {
          const targetIndex = prev.findIndex((room) => room.id === selectedChatId);
          if (targetIndex === -1) return prev;

          const currentRoom = prev[targetIndex];
          const updatedRoom = {
            ...currentRoom,
            lastMessage: normalizedText || 'Image',
            lastMessageType: uploadedImageUrl ? 'image' : 'text',
            lastSenderType: 'admin',
            lastMessageAt: sentMessageRaw?.createdAt || new Date().toISOString(),
            totalMessages: Number(currentRoom.totalMessages || 0) + 1,
          };

          const nextRooms = [...prev];
          nextRooms.splice(targetIndex, 1);
          nextRooms.unshift(updatedRoom);
          return nextRooms;
        });
      }

      setMessageText('');
      setSelectedImageFile(null);
      setImageValidationError('');
    } catch (error) {
      Toast.error(error.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const renderRoomList = () => {
    if (roomsLoading) {
      return (
        <div className="p-4 text-sm text-gray-600">Loading support requests...</div>
      );
    }

    if (roomsError) {
      return (
        <div className="p-4">
          <p className="text-sm text-red-600 mb-2">{roomsError}</p>
          <button
            type="button"
            onClick={() => setRoomsRefreshKey((value) => value + 1)}
            className="px-3 py-1.5 text-xs rounded-lg bg-brand text-white"
          >
            Retry
          </button>
        </div>
      );
    }

    if (chatRooms.length === 0) {
      return (
        <div className="p-4 text-sm text-gray-500">No support requests found.</div>
      );
    }

    return (
      <ul className="divide-y divide-[#ECECEC]">
        {chatRooms.map((room) => {
          const isSelected = selectedChatId === room.id;

          return (
            <li key={room.id}>
              <button
                type="button"
                onClick={() => setSelectedChatId(room.id)}
                className={`w-full text-left px-4 py-3 transition-colors ${
                  isSelected ? 'bg-brand/10' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  {room.avatar ? (
                    <img
                      src={room.avatar}
                      alt={room.name}
                      className="w-9 h-9 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-brand/20 text-brand flex items-center justify-center text-sm font-semibold">
                      {room.name.charAt(0).toUpperCase()}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-800 truncate">{room.name}</p>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {formatTimeShort(room.lastMessageAt)}
                      </span>
                    </div>

                    <p className="text-xs text-gray-500 truncate">{room.email || 'No email'}</p>
                    <p className="text-xs text-gray-700 mt-1 truncate">
                      {truncate(room.lastMessage || 'No messages yet')}
                    </p>
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  const getSenderLabel = (senderType) => {
    if (senderType === 'admin') return 'You';
    if (senderType === 'bot') return 'Bot';
    return selectedRoom?.name || 'User';
  };

  const renderMessages = () => {
    if (!selectedChatId) {
      return (
        <div className="h-full flex items-center justify-center text-sm text-gray-500">
          Select a support request to view the conversation.
        </div>
      );
    }

    if (messagesLoading) {
      return (
        <div className="h-full flex items-center justify-center text-sm text-gray-600">
          Loading conversation...
        </div>
      );
    }

    if (messagesError) {
      return (
        <div className="h-full flex items-center justify-center px-6">
          <p className="text-sm text-red-600">{messagesError}</p>
        </div>
      );
    }

    if (messages.length === 0) {
      return (
        <div className="h-full flex items-center justify-center text-sm text-gray-500">
          No messages in this conversation yet.
        </div>
      );
    }

    return (
      <div className="p-4 space-y-3">
        {messages.map((message) => {
          const isAdmin = message.senderType === 'admin';
          const bubbleClass = isAdmin
            ? 'bg-[#F5E8FB] border border-[#EAD5F7] text-gray-800'
            : 'bg-[#F6F1EB] border border-[#ECE0D4] text-gray-800';

          return (
            <div
              key={message.id}
              className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[78%] rounded-2xl px-3 py-2 ${bubbleClass}`}>
                <div className="flex items-center justify-between gap-3 mb-1">
                  <p className="text-xs font-semibold text-gray-600">{getSenderLabel(message.senderType)}</p>
                  <span className="text-[10px] text-gray-500 whitespace-nowrap">
                    {formatDateTime(message.createdAt)}
                  </span>
                </div>

                {message.text ? (
                  <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                ) : null}

                {message.fileUrls.length > 0 ? (
                  <div className="mt-2 space-y-1">
                    {!message.text ? (
                      <p className="text-xs text-gray-600">Image attachment</p>
                    ) : null}
                    {message.fileUrls.map((url, index) => (
                      <div
                        key={`${message.id}-file-${index}`}
                        className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 bg-white/70 border border-[#E5D5EC]"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <HiPaperClip className="w-4 h-4 text-brand shrink-0" />
                          <span className="text-xs text-gray-700 truncate max-w-[180px]">
                            {getFileNameFromUrl(url)}
                          </span>
                        </div>

                        <button
                          type="button"
                          onClick={() => downloadFile(url)}
                          className="p-1.5 rounded-md bg-brand text-white hover:opacity-90"
                          title="Download"
                        >
                          <HiDownload className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
    );
  };

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="mb-4 md:mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
            >
              <HiMenu className="w-6 h-6 text-gray-800" />
            </button>
            <h1 className="text-xl md:text-3xl font-bold text-gray-800">
              Help and Support requests
            </h1>
          </div>
        </div>

        <div className="flex-1 min-h-0 bg-white rounded-xl shadow-sm border border-[#ECECEC] overflow-hidden">
          <div className="h-full grid grid-cols-1 lg:grid-cols-[360px_1fr]">
            <div className="border-r border-[#ECECEC] flex flex-col min-h-0">
              <div className="p-3 border-b border-[#ECECEC]">
                <div className="relative">
                  <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search support requests..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-[#ECECEC] rounded-lg focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">{renderRoomList()}</div>

              {totalPages > 1 ? (
                <div className="border-t border-[#ECECEC] p-2 flex items-center justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                    disabled={currentPage === 1 || roomsLoading}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <HiChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-gray-600 px-2">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
                    disabled={currentPage === totalPages || roomsLoading}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <HiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              ) : null}
            </div>

            <div className="flex flex-col min-h-0">
              <div className="px-4 py-3 border-b border-[#ECECEC] bg-white">
                {selectedRoom ? (
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{selectedRoom.name}</p>
                    <p className="text-xs text-gray-500">{selectedRoom.email || 'No email'}</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No conversation selected</p>
                )}
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar bg-[#FCFAF8]">
                {renderMessages()}
              </div>

              <div className="border-t border-[#ECECEC] p-3 bg-white">
                <div className="space-y-2">
                  <textarea
                    value={messageText}
                    onChange={(event) => setMessageText(event.target.value)}
                    placeholder="Type your reply..."
                    rows={3}
                    className="w-full resize-none rounded-lg border border-[#ECECEC] px-3 py-2 text-sm focus:outline-none"
                    disabled={!selectedChatId || sending}
                  />

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#ECECEC] text-sm text-gray-700 cursor-pointer hover:bg-gray-50">
                        <HiPaperClip className="w-4 h-4" />
                        Attach image
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageSelection}
                          className="hidden"
                          disabled={!selectedChatId || sending}
                        />
                      </label>

                      {selectedImageFile ? (
                        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-[#F5E8FB] text-xs text-gray-700 max-w-[220px]">
                          <span className="truncate">{selectedImageFile.name}</span>
                          <button
                            type="button"
                            onClick={clearSelectedImage}
                            className="text-gray-500 hover:text-red-500"
                            disabled={sending}
                          >
                            <HiX className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <button
                      type="button"
                      onClick={handleSendMessage}
                      disabled={!selectedChatId || sending}
                      className="px-4 py-2 rounded-lg bg-brand text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sending ? 'Sending...' : 'Send'}
                    </button>
                  </div>

                  {imageValidationError ? (
                    <p className="text-xs text-red-600">{imageValidationError}</p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpSupportRequests;
